import {Overlay, OverlayConfig, OverlayRef} from "@angular/cdk/overlay";
import {BehaviorSubject, filter} from "rxjs";
import {ComponentRef, ViewContainerRef} from "@angular/core";
import {EksternEllerInternLenkeSpesifikasjon, Lenkeinformasjon, RiktekstRedigeringLenkeRedigerKomponent} from "./lenke-rediger";
import {Tastatur} from "felles-ng2/støttefunksjoner/tastaturhendelser";
import {ComponentPortal} from "@angular/cdk/portal";
import {GruppeId} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {nil} from "felles-ng2/typer/nullish";
import {tomFunksjon} from "felles-ng2/støttefunksjoner/funksjoner";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {map} from "rxjs/operators";

/** Hjelpeklasse for visning av {@link RiktekstRedigeringLenkeRedigerKomponent} i et flytende panel */
export class Lenkepanel {

    readonly vises = new BehaviorSubject(false);
    /** Aktivt panel, null dersom intet panel vises */
    private aktivt: AktivtLenkepanel | null = null;

    /** Oppretter ny instans */
    constructor(private overlay: Overlay, private viewContainerRef: ViewContainerRef) {
    }

    /** Viser lenkepanelet uten støtte for å redigere lenke.
     *  Instansierer et material Overlay som tegner ut {@link RiktekstRedigeringLenkeRedigerKomponent} komponenten.
     *  @param lenkeinfo informasjon om lenken
     *  @param ankerElement Elementet som panelet vises i underkant av */
    visUtenRedigerStøtte(lenkeinfo: Lenkeinformasjon, ankerElement: HTMLElement): void {
        this.vis(lenkeinfo, ankerElement, null, true, {vedSetAdresse: tomFunksjon, vedSlettLenke: tomFunksjon, vedLukket: tomFunksjon});
    }

    /** Viser lenkepanelet med støtte for å redigere lenke.
     *  Instansierer et material Overlay som tegner ut {@link RiktekstRedigeringLenkeRedigerKomponent} komponenten.
     *  @param lenkeinfo informasjon om lenken
     *  @param ankerElement Elementet som panelet vises i underkant av
     *  @param gruppeId dersom angitt vil bruker kunne søke frem og legge inn lenker til andre entiteter
     *  @param hendelser metoder som kalles ved ulike hendelser i rediger-guien */
    visMedRedigerStøtte(lenkeinfo: Lenkeinformasjon, ankerElement: HTMLElement, gruppeId: GruppeId | nil, hendelser: Hendelser): void {
        this.vis(lenkeinfo, ankerElement, gruppeId, false, hendelser);
    }

    /** Html-elementet som omslutter panel-innholdet, null dersom panelet ikke vises */
    panelElement(): HTMLElement | null {
        return this.aktivt?.panel.overlayElement ?? null;
    }

    /** Sann dersom bruker har aktivert forhåndvisning av lenke */
    forhåndsvisningErAktivert(): boolean {
        return erIkkeNil(this.aktivt?.lenkeRedigerKompRef.instance.forhaandsvisningAvTreff());
    }

    /** lukker lenkepanelet */
    lukk() {
        this.vises.next(false);
        if (erIkkeNil(this.aktivt)) {
            this.aktivt.panel.dispose();
            this.aktivt.hendelsehåndtering.vedLukket();
        }
        this.aktivt = null;
    }

    // private hjelpemetoder

    private vis(lenkeinfo: Lenkeinformasjon, ankerElement: HTMLElement, gruppeId: GruppeId | nil, kunVisModus: boolean, hendelsehåndtering: Hendelser): void {
        this.lukk(); //lukker event. eksisterende lenkepanel

        const panel = this.overlay.create(this.panelkonfigurasjon(ankerElement));
        //lukk forhåndsvisning ved esc (lytter termineres av forhåndsvisningPanel.dispose, derfor ingen takeUntilDestroyed)
        panel.keydownEvents()
          .pipe(filter(event => event.key == Tastatur.Escape))
          .subscribe(() => this.lukk());

        //lukk panelet dersom klikket er utenfor dette panelet, men ikke dersom klikke skjer i annet panel som er barn av dette panelet,
        // feks mat-date-range-picker eller RiktekstRedigeringLenkeRedigerKomponent.forhåndsvisningPanel
        panel.outsidePointerEvents()
          .pipe(
            map(event => event.target as HTMLElement),
            filter(elementKlikketPå => this.erKlikkSomSkalLukkePanelet(elementKlikketPå, ankerElement, panel)))
          .subscribe(() => this.lukk());

        //instansier og konfigurer komponent
        const lenkeRedigerKompRef = panel.attach(new ComponentPortal(RiktekstRedigeringLenkeRedigerKomponent, this.viewContainerRef));
        lenkeRedigerKompRef.setInput('eksisterendeLenke', lenkeinfo);
        lenkeRedigerKompRef.setInput('kunVisModus', kunVisModus);
        lenkeRedigerKompRef.setInput('gruppeId', gruppeId);
        lenkeRedigerKompRef.setInput('panel', panel);
        lenkeRedigerKompRef.instance.vedSlettLenke.subscribe(() => {
            hendelsehåndtering.vedSlettLenke();
            this.lukk();
        });
        lenkeRedigerKompRef.instance.vedSetAdresse.subscribe(spesifikasjon => {
            hendelsehåndtering.vedSetAdresse(spesifikasjon);
            this.lukk();
        });
        this.vises.next(true);

        this.aktivt = {panel, lenkeRedigerKompRef, hendelsehåndtering};
    }

    //private hjelpemetoder

    private panelkonfigurasjon(ankerElement: HTMLElement): OverlayConfig {
        const offsetY = 5;
        return {
            //uten backdrop da vi ikke ønsker at bruker ikke kan markere tekst mens panelet er åpent
            hasBackdrop: false,
            scrollStrategy: this.overlay.scrollStrategies.reposition(),
            positionStrategy: this.overlay
              .position()
              .flexibleConnectedTo(ankerElement)
              .withGrowAfterOpen(true)
              .withFlexibleDimensions(true)
              .withViewportMargin(20)
              .withDefaultOffsetY(offsetY)
              //angir hvilket punkt på forhåndsvisningFragment (overlay) som skal plasseres på hvilket punkt på origin (ankerElement)
              //the "best" position will be selected based on how well the overlay would fit within the viewport.
              .withPositions([
                  //plasser panel nedenfor og i flukt med starten på lenken
                  {originX: 'start', overlayX: 'start', originY: 'bottom', overlayY: 'top'},
                  //eller, plasser panel nedenfor og sentrert under lenken
                  {originX: 'center', overlayX: 'center', originY: 'bottom', overlayY: 'top'},
                  //eller, plasser panel nedenfor og i flukt med slutten på lenken
                  {originX: 'end', overlayX: 'end', originY: 'bottom', overlayY: 'top'},
                  //plasser panel over og i flukt med starten på lenken
                  {originX: 'start', overlayX: 'start', originY: 'top', overlayY: 'bottom'},
                  //eller, plasser panel over og sentrert under lenken
                  {originX: 'center', overlayX: 'center', originY: 'top', overlayY: 'bottom'},
                  //eller, plasser panel over og i flukt med slutten på lenken
                  {originX: 'end', overlayX: 'end', originY: 'top', overlayY: 'bottom'},
              ])
        };
    }

    private erKlikkSomSkalLukkePanelet(elementKlikketPå: HTMLElement, ankerElement: HTMLElement, panel: OverlayRef) {
        //ikke lukke dersom bruker klikker på ankerelementet panelet er tilknyttet
        if (elementKlikketPå == ankerElement || ankerElement.contains(elementKlikketPå))
            return false;

        //ikke lukk dersom bruker klikket på et element i dette panelet
        if (panel.overlayElement.contains(elementKlikketPå))
            return false;

        //ikke lukk dersom bruker klikket på et panel som er barn av dette panelet (dvs ligger etter i dom-strukturen)
        //(feks panel opprettet av mat-date-range-picker eller RiktekstRedigeringLenkeRedigerKomponent.forhåndsvisningPanel)
        const panelKontainer = panel.overlayElement.closest('.cdk-overlay-pane')!; //! fordi vi vet at dette er et overlay-pane
        const panelKontainerKlikketPå = elementKlikketPå.closest('.cdk-overlay-pane');
        if (erIkkeNil(panelKontainerKlikketPå) && panelKontainer.compareDocumentPosition(panelKontainerKlikketPå) == Node.DOCUMENT_POSITION_FOLLOWING)
            return false;

        return true;
    }
}

type AktivtLenkepanel = {
    panel: OverlayRef;
    lenkeRedigerKompRef: ComponentRef<RiktekstRedigeringLenkeRedigerKomponent>;
    hendelsehåndtering: Hendelser;
}

export type Hendelser = {
    vedSlettLenke: () => void;
    vedSetAdresse: (spesifikasjon: EksternEllerInternLenkeSpesifikasjon) => void;
    vedLukket: () => void;
}
